import {getMonolithApiHeaders} from '@udemy-v2/api-fetch';

import {getRuntimeConfig} from '@/app/api/config/get-config';
import {toAcceptLanguage} from '@/lib/accept-language';

/**
 * Shared `/api-2.0/` REST fetch helper — used by both client hooks and server-fetch DAL functions.
 *
 * - Client: headers come from `getMonolithApiHeaders()` (cookie-derived).
 * - Server: `getMonolithApiHeaders()` is skipped (reads `document.cookie`, unavailable in Node),
 *   and `url` is resolved to an absolute one via `MONOLITH_URL` — Node's `fetch` has no page
 *   origin to resolve a relative path against.
 * - `locale`, if passed, sets `Accept-Language` in both contexts.
 * - Throws on non-2xx (callers map this to `null`). 204 resolves to `undefined`.
 */
export async function monolithFetch<T>(url: string, locale?: string): Promise<T> {
    const isServer = typeof window === 'undefined';
    const headers: Record<string, string> = isServer ? {} : getMonolithApiHeaders();

    if (locale) {
        headers['Accept-Language'] = toAcceptLanguage(locale);
    }

    const resolvedUrl = isServer ? new URL(url, getRuntimeConfig('MONOLITH_URL')).toString() : url;
    const response = await fetch(resolvedUrl, {headers});
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    if (response.status === 204) {
        return undefined as T;
    }
    return (await response.json()) as T;
}
